import type { ComponentProps, ComponentPropsWithoutRef } from 'react'

import Badge from '@components/atoms/Badge'

import type { ComponentDoc, PropDocs } from '../types'

type BadgeProps = ComponentProps<typeof Badge>

type BadgeOwnProps = Omit<BadgeProps, keyof ComponentPropsWithoutRef<'span'>> & Pick<BadgeProps, 'color'>

const props: PropDocs<BadgeOwnProps> = {
  color: {
    type: "'gray' | 'muted' | 'neutral' | 'success' | 'primary' | 'info' | 'blue' | 'warning' | 'error'",
    default: "'gray'",
    description: 'Sets the background and text colour.',
  },
  shape: { type: "'pill' | 'rounded'", default: "'pill'", description: 'Fully rounded, or 6px corners.' },
  size: { type: "'md'", default: "'md'", description: 'Only one size today: 12px text, 8px/4px padding.' },
  bordered: { type: 'boolean', default: 'false', description: 'Adds a 1px border in the same hue as the fill.' },
  leadingIcon: { type: 'IconTypes', description: 'Sprite glyph before the label, 14px.' },
  trailingIcon: { type: 'IconTypes', description: 'Sprite glyph after the label, 14px.' },
  ref: { type: 'Ref<HTMLSpanElement>', description: 'Forwarded to the span.' },
}

const doc: ComponentDoc<BadgeProps> = {
  slug: 'badge',
  name: 'Badge',
  tier: 'atom',
  importStatement: "import Badge from '@components/atoms/Badge'",
  sourcePath: 'app/frontend/components/atoms/Badge.tsx',
  component: Badge,
  props,
  examples: [
    {
      title: 'Colours',
      variants: [
        { children: 'Gray' },
        { color: 'muted', children: 'Muted' },
        { color: 'neutral', children: 'Neutral' },
        { color: 'primary', children: 'Primary' },
        { color: 'info', children: 'Info' },
        { color: 'blue', children: 'Blue' },
        { color: 'success', children: 'Success' },
        { color: 'warning', children: 'Warning' },
        { color: 'error', children: 'Error' },
      ],
    },
    {
      title: 'Shapes and borders',
      variants: [
        { color: 'success', shape: 'rounded', children: 'Rounded' },
        { color: 'warning', bordered: true, children: 'Bordered pill' },
        { color: 'error', shape: 'rounded', bordered: true, children: 'Bordered rounded' },
      ],
    },
    {
      title: 'With icons',
      variants: [
        { leadingIcon: 'check', children: 'Leading icon' },
        { trailingIcon: 'chevron-right', children: 'Trailing icon' },
      ],
    },
  ],
  a11y: ['Renders a plain <span> with no ARIA role, label, or live region.'],
}

export default doc
